import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../../app/store'
import { CartState } from '../../types'
import brandManager from '../../services/brandManager'
import { buildEndpoint, getPlatformHeader } from '../../utils/apiUtils'
import { getAuthToken } from '../../utils/secureStorage'

// Helper function to unwrap API response and remove static fields
function unwrapResponse(response: any): any {
  // If response has the wrapper structure {message, descriptiveMessage, data}, return just data
  if (response && typeof response === 'object' && 'data' in response && 'message' in response) {
    return response.data
  }
  // Otherwise return response as-is
  return response
}

// Enhanced baseQuery with error logging
const baseQueryWithErrorLogging = fetchBaseQuery({
  baseUrl: brandManager.apiUrl,
  prepareHeaders: (headers, { getState }) => {
    // Try to get token from Redux state first
    let token = (getState() as RootState).auth.token
    
    // If not in Redux state (e.g., after page refresh before rehydration), get from secure storage
    if (!token) {
      token = getAuthToken()
    }
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    headers.set('lang', localStorage.getItem('language') || 'ar')
    headers.set('platform', getPlatformHeader())
    return headers
  },
})

// Wrap baseQuery to add error logging
const baseQuery = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQueryWithErrorLogging(args, api, extraOptions)
  
  // Log 500 errors with detailed information
  if (result.error && 'status' in result.error && result.error.status === 500) {
    const url = typeof args === 'string' ? args : args.url
    const method = typeof args === 'string' ? 'GET' : (args.method || 'GET')
    const fullUrl = `${brandManager.apiUrl}${url}`
    const errorData = result.error.data as any
  }
  
  return result
}

// Flutter-style cart API service - exact match
export const cartApi = createApi({
  reducerPath: 'cartApi',
  baseQuery,
  tagTypes: ['Cart'],
  endpoints: (builder) => ({
    // Flutter-style cart data with optional coupon and table - exact match
    getCartData: builder.query<CartState, { 
      userId: number; 
      couponCode?: string; 
      tableId?: number 
    }>({
      query: ({ userId, couponCode, tableId }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}`, userId)
        const params = new URLSearchParams()
        
        if (couponCode && couponCode.trim()) {
          params.append('coboneCode', couponCode)
        }
        if (tableId && tableId > 0) {
          params.append('tableId', tableId.toString())
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`
        }
        
        return url
      },
      transformResponse: unwrapResponse,
      providesTags: ['Cart'],
    }),
    
    // Flutter-style cart data only (no modifications) - exact match
    getCartDataOnly: builder.query<CartState, number>({
      query: (userId) => buildEndpoint(`/cashier/carts/user/${userId}`, userId),
      transformResponse: unwrapResponse,
      providesTags: ['Cart'],
    }),
    
    // Flutter-style add to cart - exact match
    addToCart: builder.mutation<CartState, { 
      userId: number; 
      productId: number; 
      quantity: number;
      mealId?: number;
      additionals?: number[];
      notes?: string;
    }>({
      query: ({ userId, productId, quantity, mealId, additionals, notes }) => ({
        url: buildEndpoint(`/cashier/carts/user/${userId}/items`, userId),
        method: 'POST',
        body: { 
          product_id: productId, 
          quantity,
          meal_id: mealId,
          additionals: additionals || [],
          notes: notes || ''
        },
      }),
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Helper function to clean itemId (remove "i" prefix if present)
    // Flutter-style update cart item - exact match
    updateCartItem: builder.mutation<CartState, { 
      userId: number; 
      itemId: string; 
      quantity: number;
      mealId?: number;
      additionals?: number[];
      notes?: string;
    }>({
      query: ({ userId, itemId, quantity, mealId, additionals, notes }) => {
        // Clean itemId (remove "i" prefix if present, e.g., "i93" -> "93")
        const cleanedItemId = itemId.trim().startsWith('i') && itemId.trim().length > 1 
          ? itemId.trim().substring(1) 
          : itemId.trim()
        
        return {
          url: buildEndpoint(`/cashier/carts/user/${userId}/items/${cleanedItemId}`, userId),
          method: 'PUT',
          body: { 
            quantity,
            meal_id: mealId,
            additionals: additionals || [],
            notes: notes || ''
          },
        }
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Flutter-style remove from cart - exact match
    removeFromCart: builder.mutation<CartState, { userId: number; itemId: string }>({
      query: ({ userId, itemId }) => {
        // Clean itemId (remove "i" prefix if present, e.g., "i93" -> "93")
        const cleanedItemId = itemId.trim().startsWith('i') && itemId.trim().length > 1 
          ? itemId.trim().substring(1) 
          : itemId.trim()
        
        return {
          url: buildEndpoint(`/cashier/carts/user/${userId}/items/${cleanedItemId}`, userId),
          method: 'DELETE',
        }
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Flutter-style clear cart - exact match
    clearCart: builder.mutation<CartState, number>({
      query: (userId) => ({
        url: buildEndpoint(`/cashier/carts/user/${userId}`, userId),
        method: 'DELETE',
      }),
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Validate coupon code before applying (matches Flutter's CouponValidationService)
    validateCoupon: builder.mutation<{
      success: boolean;
      message?: string;
      discount?: number;
      data?: any;
    }, {
      couponCode: string;
      amount: number;
      userId?: number;
    }>({
      query: ({ couponCode, amount, userId }) => {
        const body: any = {
          code: couponCode,
          amount: amount.toString(),
        }
        
        if (userId && userId > 0) {
          body.userId = userId
        }
        
        return {
          url: buildEndpoint('/cashier/cobones/check'),
          method: 'POST',
          body,
        }
      },
      // Don't unwrap response here, we need to check the structure
    }),
    
    // Flutter-style apply coupon with validation - exact match
    applyCouponWithValidation: builder.mutation<CartState, {
      userId: number;
      couponCode: string;
      tableId?: number;
    }>({
      query: ({ userId, couponCode, tableId }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}`, userId)
        const params = new URLSearchParams()
        
        params.append('coboneCode', couponCode)
        if (tableId && tableId > 0) {
          params.append('tableId', tableId.toString())
        }
        
        url += `?${params.toString()}`
        return url
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Flutter-style apply table - exact match
    applyTable: builder.mutation<CartState, {
      userId: number;
      tableId: number;
      couponCode?: string;
    }>({
      query: ({ userId, tableId, couponCode }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}`, userId)
        const params = new URLSearchParams()
        
        params.append('tableId', tableId.toString())
        if (couponCode && couponCode.trim()) {
          params.append('coboneCode', couponCode)
        }
        
        url += `?${params.toString()}`
        return url
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Flutter-style remove coupon - exact match
    removeCoupon: builder.mutation<CartState, {
      userId: number;
      tableId?: number;
    }>({
      query: ({ userId, tableId }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}`, userId)
        const params = new URLSearchParams()
        
        if (tableId && tableId > 0) {
          params.append('tableId', tableId.toString())
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`
        }
        return url
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Flutter-style remove table - exact match
    removeTable: builder.mutation<CartState, {
      userId: number;
      couponCode?: string;
    }>({
      query: ({ userId, couponCode }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}`, userId)
        const params = new URLSearchParams()
        
        if (couponCode && couponCode.trim()) {
          params.append('coboneCode', couponCode)
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`
        }
        return url
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Enhanced cart operations for better UX
    addItemToCart: builder.mutation<CartState, {
      userId: number;
      productId: number;
      quantity: number;
      sizeId?: number;
      additionals?: Array<{
        id: number;
        quantity: number;
      }>;
      notes?: string;
    }>({
      query: ({ userId, productId, quantity, sizeId, additionals, notes }) => ({
        url: buildEndpoint(`/cashier/carts/user/${userId}/items`, userId),
        method: 'POST',
        body: {
          product_id: productId,
          quantity,
          size_id: sizeId,
          additionals: additionals || [],
          notes: notes || ''
        },
      }),
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Update cart item quantity
    updateItemQuantity: builder.mutation<CartState, {
      userId: number;
      itemId: string;
      quantity: number;
    }>({
      query: ({ userId, itemId, quantity }) => {
        // Clean itemId (remove "i" prefix if present, e.g., "i93" -> "93")
        const cleanedItemId = itemId.trim().startsWith('i') && itemId.trim().length > 1 
          ? itemId.trim().substring(1) 
          : itemId.trim()
        
        return {
          url: buildEndpoint(`/cashier/carts/user/${userId}/items/${cleanedItemId}`, userId),
          method: 'PUT',
          body: { quantity },
        }
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Remove cart item
    removeCartItem: builder.mutation<CartState, {
      userId: number;
      itemId: string;
    }>({
      query: ({ userId, itemId }) => {
        // Clean itemId (remove "i" prefix if present, e.g., "i93" -> "93")
        const cleanedItemId = itemId.trim().startsWith('i') && itemId.trim().length > 1 
          ? itemId.trim().substring(1) 
          : itemId.trim()
        
        return {
          url: buildEndpoint(`/cashier/carts/user/${userId}/items/${cleanedItemId}`, userId),
          method: 'DELETE',
        }
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Apply discount code
    applyDiscount: builder.mutation<CartState, {
      userId: number;
      discountCode: string;
      tableId?: number;
    }>({
      query: ({ userId, discountCode, tableId }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}/discount`, userId)
        const params = new URLSearchParams()
        
        params.append('code', discountCode)
        if (tableId && tableId > 0) {
          params.append('tableId', tableId.toString())
        }
        
        url += `?${params.toString()}`
        return url
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Remove discount
    removeDiscount: builder.mutation<CartState, {
      userId: number;
      tableId?: number;
    }>({
      query: ({ userId, tableId }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}/discount`, userId)
        const params = new URLSearchParams()
        
        if (tableId && tableId > 0) {
          params.append('tableId', tableId.toString())
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`
        }
        return url
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Apply table to cart
    applyTableToCart: builder.mutation<CartState, {
      userId: number;
      tableId: number;
      discountCode?: string;
    }>({
      query: ({ userId, tableId, discountCode }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}/table`, userId)
        const params = new URLSearchParams()
        
        params.append('tableId', tableId.toString())
        if (discountCode && discountCode.trim()) {
          params.append('discountCode', discountCode)
        }
        
        url += `?${params.toString()}`
        return url
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Remove table from cart
    removeTableFromCart: builder.mutation<CartState, {
      userId: number;
      discountCode?: string;
    }>({
      query: ({ userId, discountCode }) => {
        let url = buildEndpoint(`/cashier/carts/user/${userId}/table`, userId)
        const params = new URLSearchParams()
        
        if (discountCode && discountCode.trim()) {
          params.append('discountCode', discountCode)
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`
        }
        return url
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // New add-to-cart endpoint with items array format
    addToCartWithItems: builder.mutation<any, {
      items: Array<{
        itemId: number; // meal id (18 or 19), not product id (9)
        quantity: number;
        meals: Array<{
          mealId: number;
          additionals: number[]; // array of numbers [6], not objects [{id: 6}]
        }>;
      }>;
      platform: string;
      typeId: number;
      tableId?: number;
      branchId: number;
      userId?: number;
      creatorId?: number;
      addressId?: number;
    }>({
      query: (body) => ({
        url: buildEndpoint('/cashier/add-to-cart'),
        method: 'POST',
        body,
      }),
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Get cart by user ID (matches the second API)
    getCartByUserId: builder.query<CartState, number>({
      query: (userId) => buildEndpoint(`/cashier/carts/user/${userId}`, userId),
      transformResponse: unwrapResponse,
      providesTags: ['Cart'],
    }),
    
    // Change cart type - used when changing order type with items in cart
    changeCartType: builder.mutation<any, {
      cartId: number | string;
      typeId: number;
      branchId: number;
      userId?: number;
      addressId?: number;
    }>({
      query: ({ cartId, typeId, branchId, userId, addressId }) => {
        const body: Record<string, number> = {
          typeId,
          branchId,
        }
        if (userId && userId > 0) {
          body.userId = userId
        }
        if (typeId === 1 && addressId && addressId > 0) {
          body.addressId = addressId
        }
        return {
          url: buildEndpoint(`/cashier/carts/${cartId}/change-type`),
          method: 'PUT',
          body,
        }
      },
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
    
    // Update cart user - transfer cart from cashier to customer
    updateCartUser: builder.mutation<any, {
      cartId: number | string;
      userId: number;
    }>({
      query: ({ cartId, userId }) => ({
        url: buildEndpoint(`/cashier/carts/${cartId}/update-user`),
        method: 'PUT',
        body: {
          userId,
        },
      }),
      transformResponse: unwrapResponse,
      invalidatesTags: ['Cart'],
    }),
  }),
})

export const {
  useGetCartDataQuery,
  useLazyGetCartDataQuery,
  useGetCartDataOnlyQuery,
  useAddToCartMutation,
  useUpdateCartItemMutation,
  useRemoveFromCartMutation,
  useClearCartMutation,
  useValidateCouponMutation,
  useApplyCouponWithValidationMutation,
  useApplyTableMutation,
  useRemoveCouponMutation,
  useRemoveTableMutation,
  useAddItemToCartMutation,
  useUpdateItemQuantityMutation,
  useRemoveCartItemMutation,
  useApplyDiscountMutation,
  useRemoveDiscountMutation,
  useApplyTableToCartMutation,
  useRemoveTableFromCartMutation,
  useAddToCartWithItemsMutation,
  useGetCartByUserIdQuery,
  useLazyGetCartByUserIdQuery,
  useChangeCartTypeMutation,
  useUpdateCartUserMutation,
} = cartApi